import type { ReactNode } from 'react';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Field.css';

/**
 * The chrome every form control shares: the label and error row above, the
 * bordered box that owns the hover and focus states, and the hint row below.
 *
 * Not part of the design system's public surface — screens use Input, Textarea
 * and friends. This is what those are built from, so that a change to how a
 * focused field looks happens once instead of once per control.
 */

/** Both ids hang off the control's, so a control can point at its own messages. */
const errorIdFor = (controlId: string) => `${controlId}-error`;
const hintIdFor = (controlId: string) => `${controlId}-hint`;

/**
 * The `aria-describedby` for the control inside a Field. Lists only the rows that
 * actually have text — pointing at an empty one makes a screen reader announce
 * "blank" after the value.
 */
export function fieldDescribedBy(
  controlId: string,
  { error, hint }: { error?: string; hint?: string },
): string | undefined {
  const ids = [error ? errorIdFor(controlId) : null, hint ? hintIdFor(controlId) : null];
  return ids.filter(Boolean).join(' ') || undefined;
}

export interface FieldProps {
  /** The id of the control inside the box; the label points at it. */
  controlId: string;
  label?: string;
  /** Sits opposite the label rather than under the field, so the box does not
   * move when a message appears. Also turns the box red. */
  error?: string;
  /** Opposite the label when there is no error — “Forgot password?”. */
  labelEnd?: ReactNode;
  /** Quiet note under the field. Shows alongside an error, not instead of it. */
  hint?: string;
  /** Sits opposite the hint. A character counter, typically. */
  hintEnd?: ReactNode;
  /** Greys the box out. The control inside still needs its own `disabled`. */
  disabled?: boolean;
  /**
   * Keeps the label and hint rows occupied even when empty, so fields sitting
   * side by side line up whether or not each one has a label.
   */
  reserveSpace?: boolean;
  /** Lets a multi-line control fill the box instead of sitting centred in it. */
  multiline?: boolean;
  /**
   * Take the space left over on a shared row instead of the whole width. A
   * search box beside a button is the case this exists for; on a narrow screen
   * the row still wraps and the field goes back to full width.
   */
  grow?: boolean;
  /** Sits on the input row, centred on the box — Update, a unit, a short CTA. */
  action?: ReactNode;
  /** No border or fill — the control sits flush inside a parent shell (e.g. participant table). */
  borderless?: boolean;
  /** Parent id. Label, error, hint and box become `${testId}-label` and so on. */
  testId?: string;
  className?: string;
  children: ReactNode;
}

export function Field({
  controlId,
  label,
  error,
  labelEnd,
  hint,
  hintEnd,
  disabled = false,
  reserveSpace = false,
  multiline = false,
  grow = false,
  action,
  borderless = false,
  testId,
  className,
  children,
}: FieldProps) {
  const classes = ['field'];
  if (error) {
    classes.push('field--invalid');
  }
  if (disabled) {
    classes.push('field--disabled');
  }
  if (reserveSpace) {
    classes.push('field--reserve-space');
  }
  if (multiline) {
    classes.push('field--multiline');
  }
  if (grow) {
    classes.push('field--grow');
  }
  if (borderless) {
    classes.push('field--borderless');
  }
  if (className) {
    classes.push(className);
  }

  return (
    <div className={classes.join(' ')} {...testIdAttr(testId)}>
      {(label || error || labelEnd || reserveSpace) && (
        <div className="field__header">
          <Typography
            as="label"
            role="label/field"
            className="field__label"
            htmlFor={controlId}
            {...testIdAttr(testIdFor(testId, 'label'))}
          >
            {label}
          </Typography>
          {error ? (
            <Typography
              as="span"
              role="meta/error"
              className="field__error"
              id={errorIdFor(controlId)}
              {...testIdAttr(testIdFor(testId, 'error'))}
            >
              {error}
            </Typography>
          ) : (
            labelEnd
          )}
        </div>
      )}

      {/* The border and glow live on this box rather than on the control, so
          that a prefix, a counter or an in-field button sits inside the border and
          lights up with it. */}
      {action ? (
        <div className="field__row">
          <div className="field__box" {...testIdAttr(testIdFor(testId, 'box'))}>
            {children}
          </div>
          <div className="field__action">{action}</div>
        </div>
      ) : (
        <div className="field__box" {...testIdAttr(testIdFor(testId, 'box'))}>
          {children}
        </div>
      )}

      {(hint || hintEnd || reserveSpace) && (
        <div className="field__footer">
          <Typography
            as="p"
            role="meta/helper"
            className="field__hint"
            id={hintIdFor(controlId)}
            {...testIdAttr(testIdFor(testId, 'hint'))}
          >
            {hint}
          </Typography>
          {hintEnd}
        </div>
      )}
    </div>
  );
}
